import type { AnyExtension } from '@tiptap/core';
import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Dropcursor, Gapcursor, Placeholder, TrailingNode } from '@tiptap/extensions';
import { HardBreak } from '@tiptap/extension-hard-break';
import { TextStyle } from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';
import { Bold } from 'reactjs-tiptap-editor/bold';
import { Italic } from 'reactjs-tiptap-editor/italic';
import { TextUnderline } from 'reactjs-tiptap-editor/textunderline';
import { Strike } from 'reactjs-tiptap-editor/strike';
import { Heading } from 'reactjs-tiptap-editor/heading';
import { BulletList } from 'reactjs-tiptap-editor/bulletlist';
import { OrderedList } from 'reactjs-tiptap-editor/orderedlist';
import { TaskList } from 'reactjs-tiptap-editor/tasklist';
import { Blockquote } from 'reactjs-tiptap-editor/blockquote';
import { Code } from 'reactjs-tiptap-editor/code';
import { CodeBlock } from 'reactjs-tiptap-editor/codeblock';
import { Link } from 'reactjs-tiptap-editor/link';
import { Table } from 'reactjs-tiptap-editor/table';
import { Highlight } from 'reactjs-tiptap-editor/highlight';
import { HorizontalRule } from 'reactjs-tiptap-editor/horizontalrule';
import { History } from 'reactjs-tiptap-editor/history';
import { Image } from 'reactjs-tiptap-editor/image';
import { SlashCommand } from 'reactjs-tiptap-editor/slashcommand';
import { RICH_TEXT_IMAGE_MAX_BYTES, RICH_TEXT_IMAGE_MIMES } from './image-upload-limits';

export const RICH_TEXT_HEADING_LEVELS = [1, 2, 3];

export function instanceOf(ext: unknown): AnyExtension {
  const node = ext as { configure?: (opts?: object) => AnyExtension };
  if (typeof node.configure === 'function') {
    return node.configure({});
  }
  return ext as AnyExtension;
}

/**
 * The formatting kit shared by work-item descriptions and wiki pages.
 * Callers append surface-specific extensions (Mention, Markdown, embeds).
 */
export function richTextBaseExtensions(opts: {
  placeholder: string;
  upload: (file: File) => Promise<string>;
  /** Replacement for the default code block (e.g. one with Markdown-aware serialisation). */
  codeBlock?: AnyExtension;
  /** Underline has no standard Markdown form; Markdown surfaces turn it off. Defaults to on. */
  underline?: boolean;
}): AnyExtension[] {
  return [
    Document.configure({}),
    Text.configure({}),
    Paragraph.configure({}),
    Dropcursor.configure({}),
    Gapcursor.configure({}),
    HardBreak.configure({}),
    TrailingNode.configure({}),
    ListItem.configure({}),
    TextStyle.configure({}),
    Placeholder.configure({ placeholder: opts.placeholder }),
    instanceOf(History),
    instanceOf(Bold),
    instanceOf(Italic),
    ...(opts.underline === false ? [] : [instanceOf(TextUnderline)]),
    instanceOf(Strike),
    Heading.configure({ levels: RICH_TEXT_HEADING_LEVELS }),
    instanceOf(BulletList),
    instanceOf(OrderedList),
    instanceOf(TaskList),
    instanceOf(Blockquote),
    instanceOf(Code),
    opts.codeBlock ?? instanceOf(CodeBlock),
    instanceOf(Link),
    instanceOf(Table),
    instanceOf(Highlight),
    instanceOf(HorizontalRule),
    instanceOf(SlashCommand),
    Image.configure({
      resourceImage: 'both',
      upload: opts.upload,
      acceptMimes: RICH_TEXT_IMAGE_MIMES,
      maxSize: RICH_TEXT_IMAGE_MAX_BYTES,
    }),
  ] as AnyExtension[];
}
